import i18next from 'i18next';

import { ParsedCustomFieldConfig } from '@work4all/data/lib/custom-fields';

import { CustomFieldPage } from '@work4all/models/lib/Classes/CustomFieldPage.entity';
import { IndividualFieldControlType } from '@work4all/models/lib/Enums/IndividualFieldControlType.enum';

export interface GroupedCustomFields {
  groups: CustomFieldGroup[];
}

export interface CustomFieldGroup {
  id: string | number;
  name: string;
  isDefault?: true;
  subgroups: CustomFieldSubgroup[];
}

export interface CustomFieldSubgroup {
  id: string | number;
  fields: ParsedCustomFieldConfig[];
  headline: string;
}

export function groupCustomFields(
  fields: ParsedCustomFieldConfig[]
): GroupedCustomFields {
  let currentGroup: CustomFieldGroup = null;
  let currentSubgroup: CustomFieldSubgroup = null;

  const groups: CustomFieldGroup[] = [];
  const { groupedFields, nonGroupedFields } =
    getGroupedAndNonGroupedFields(fields);

  const fieldsByGroup = getFieldsByGroup(groupedFields);

  if (nonGroupedFields.length > 0) {
    if (groupedFields.length > 0) {
      currentGroup = createGroup('individual', i18next.t('COMMON.INDIVIDUAL'));
      groups.push(currentGroup);
    } else if (!isCaptionField(fields[0])) {
      currentGroup = createDefaultGroup();
      currentSubgroup = createDefaultSubgroup();

      currentGroup.subgroups.push(currentSubgroup);
      groups.push(currentGroup);
    }
  }

  for (const field of nonGroupedFields) {
    if (isCaptionField(field)) {
      if (groupedFields.length > 0) {
        currentSubgroup = createHeadlineSubgroup(field.name);
        currentGroup.subgroups.push(currentSubgroup);
      } else {
        currentGroup = createGroup(field.id, field.name);
        currentSubgroup = createDefaultSubgroup();

        currentGroup.subgroups.push(currentSubgroup);

        groups.push(currentGroup);
      }
    } else if (isNewLineField(field)) {
      currentSubgroup = createSubgroup(field.id);

      currentGroup.subgroups.push(currentSubgroup);
    } else {
      currentSubgroup.fields.push(field);
    }
  }

  for (const group of fieldsByGroup) {
    currentGroup = createGroup(group.data.id, group.data.name);

    if (group.fields.length > 0 && !isCaptionField(group.fields[0])) {
      currentSubgroup = createDefaultSubgroup();
      currentGroup.subgroups.push(currentSubgroup);
    }

    for (const field of group.fields) {
      if (isCaptionField(field)) {
        currentSubgroup = createHeadlineSubgroup(field.name);
        currentGroup.subgroups.push(currentSubgroup);
      } else if (isNewLineField(field)) {
        currentSubgroup = createSubgroup(field.id);
        currentGroup.subgroups.push(currentSubgroup);
      } else {
        currentSubgroup.fields.push(field);
      }
    }

    groups.push(currentGroup);
  }

  return { groups };
}

function isCaptionField(field: ParsedCustomFieldConfig): boolean {
  return field.fieldType === IndividualFieldControlType.CAPTION;
}

function isNewLineField(field: ParsedCustomFieldConfig): boolean {
  return field.fieldType === IndividualFieldControlType.ZEILENUMBRUCH;
}

function createGroup(
  id: string | number,
  name: string,
  isDefault?: true
): CustomFieldGroup {
  return { id, name, isDefault, subgroups: [] };
}

function createDefaultGroup(): CustomFieldGroup {
  return createGroup('default', '', true);
}

function createSubgroup(
  id: string | number,
  headline?: string
): CustomFieldSubgroup {
  return { id, headline, fields: [] };
}

function createDefaultSubgroup(): CustomFieldSubgroup {
  return createSubgroup('default');
}

function createHeadlineSubgroup(headline: string): CustomFieldSubgroup {
  return createSubgroup('headline', headline);
}

function getGroupedAndNonGroupedFields(fields: ParsedCustomFieldConfig[]): {
  groupedFields: ParsedCustomFieldConfig[];
  nonGroupedFields: ParsedCustomFieldConfig[];
} {
  const groupedFields: ParsedCustomFieldConfig[] = [];
  const nonGroupedFields: ParsedCustomFieldConfig[] = [];

  for (const field of fields) {
    const isGrouped = Boolean(field.customFieldPage);

    if (isGrouped) {
      groupedFields.push(field);
    } else {
      nonGroupedFields.push(field);
    }
  }

  return { groupedFields, nonGroupedFields } as const;
}

function getFieldsByGroup(fields: ParsedCustomFieldConfig[]): {
  data: CustomFieldPage;
  fields: ParsedCustomFieldConfig[];
}[] {
  const data: {
    [key: number]: {
      data: CustomFieldPage;
      fields: ParsedCustomFieldConfig[];
    };
  } = {};

  for (const field of fields) {
    let group = data[field.customFieldPage.id];

    if (!group) {
      data[field.customFieldPage.id] = {
        data: field.customFieldPage,
        fields: [],
      };

      group = data[field.customFieldPage.id];
    }

    group.fields.push(field);
  }

  return Object.values(data);
}
